<template>
  <div
    class="relative z-10 flex h-screen transform flex-col items-end justify-between px-2 pt-8 pb-2 transition-all"
    :class="submenuClass"
  >
    <div class="menu-column flex h-full flex-col justify-between">
      <div ref="menuWrapper" class="menu-wrapper space-y-8 flex flex-col">
        <search-input
          v-if="searchable && isExpanded"
          v-model="searchQuery"
          class="px-2 w-50"
          :search-term="$t('COMMON.SETTINGS')"
          no-margin
          no-padding
        />

        <div
          v-for="(section, index) in filteredOptions"
          :key="section?.name"
          class="overflow-hidden"
        >
          <access-control :roles="section?.acl || allRoles">
            <div>
              <div
                v-if="section?.name"
                class="mb-4 pl-3 text-xxs font-semibold text-gray-500 uppercase"
                :class="{ 'mt-7': index !== 0 }"
              >
                {{ section.name }}
              </div>
              <div v-for="(option, i) in section?.options" :key="i">
                <access-control :roles="option.acl || allRoles">
                  <template
                    v-if="
                      !option.confirm && !option.tabs && !option.tabComponent
                    "
                  >
                    <router-link v-slot="{ isActive }" :to="option.link || ''">
                      <sidebar-submenu-item
                        class="my-1-5"
                        :option="option"
                        :active="isActive"
                      />
                    </router-link>
                  </template>
                  <template
                    v-else-if="
                      !option.confirm && (option.tabs || option.tabComponent)
                    "
                  >
                    <lf-details
                      :model-value="isTabOpen(option.value ?? option.label)"
                      class="justify-between"
                      @update:model-value="
                        (val: boolean) =>
                          setTabOpen(option.value ?? option.label, val)
                      "
                    >
                      <template #summary="{ isOpen, setOpen }">
                        <router-link
                          :to="option?.link || ''"
                          class="flex w-full"
                          @click="emit('tab-click', option.value)"
                        >
                          <sidebar-submenu-item :option="option" />
                        </router-link>
                        <div
                          class="flex w-10 cursor-pointer items-center justify-center rounded-sm px-2 py-4 hover:bg-blue-100"
                          @click="() => setOpen(!isOpen)"
                        >
                          <icon-chevron-up
                            class="transform transition-all"
                            :class="{ 'rotate-180': isOpen }"
                            fill="#868D9C"
                          />
                        </div>
                      </template>
                      <template v-if="option.tabs">
                        <sidebar-submenu-tab
                          v-for="tab in option.tabs"
                          :key="tab"
                          :option="option"
                          :tab="tab"
                          :active-tab="activeTab"
                          @click="emit('update:activeTab', tab)"
                        />
                        <router-link
                          v-for="tabLink in option.additionalTabLinks"
                          :key="tabLink.link"
                          :to="tabLink?.link || ''"
                          class="flex w-full"
                        >
                          <side-menu-item
                            class="m-0 pl-7 leading-4"
                            :active="activeTab === tabLink.label"
                            is-slim
                          >
                            <span class="text-xs">
                              {{ tabLink.label }}
                            </span>
                          </side-menu-item>
                        </router-link>
                      </template>
                      <component
                        :is="option.tabComponent"
                        v-else-if="option.tabComponent"
                        v-bind="option.tabComponentProps"
                      />
                    </lf-details>
                  </template>
                  <template v-else>
                    <confirm-modal
                      v-if="showConfirmModal"
                      :title="option.confirm?.title ?? option.label"
                      :description="option.confirm?.description ?? ''"
                      :confirm-button="option.confirm?.btnText ?? 'Submit'"
                      :confirm-button-type="
                        option.confirm?.btnType ?? 'success'
                      "
                      :close="() => (showConfirmModal = false)"
                      @confirm="executeHandler(option)"
                    />
                    <sidebar-submenu-item
                      class="my-1-5"
                      :option="option"
                      :active="isOptionActive(option)"
                      @click="showConfirmModal = true"
                    />
                  </template>
                </access-control>
              </div>
            </div>
          </access-control>
        </div>
      </div>
      <slot name="bottom-option" />
    </div>

    <div v-if="isExpandable" class="absolute top-[80%] right-0 z-50">
      <expand-button-white
        class="transform cursor-pointer shadow-xs"
        :class="{
          'translate-x-[50%]': !isExpanded,
        }"
        :expanded="isExpanded"
        color="white"
        data-cy="sidebar-expand-button"
        @click="handleToggleClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import {
  CLIENT_ROLE_GROUP,
  FUNDER_ROLE_GROUP,
  LENDFLOW_ROLE_GROUP,
} from "@/helpers/constants";

import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import SidebarSubmenuItem from "@/components/sidebar/SidebarSubmenuItem.vue";
import SidebarSubmenuTab from "@/components/sidebar/SidebarSubmenuTab.vue";
import LfDetails from "@/components/ui/LfDetails.vue";
import SideMenuItem from "@/components/ui/SideMenuItem.vue";
import ExpandButtonWhite from "@/components/ui/buttons/ExpandButtonWhite.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

import type {
  ISidebarSubmenuOption,
  ISidebarSubmenuSection,
} from "@/models/common";

const allRoles = [
  ...LENDFLOW_ROLE_GROUP,
  ...CLIENT_ROLE_GROUP,
  ...FUNDER_ROLE_GROUP,
];
const { path, matched } = useRoute();

const {
  options = [],
  hasTabs = false,
  activeTab = "",
  isExpandable = true,
  openTabValue = "",
  searchable = false,
} = defineProps<{
  options?: (ISidebarSubmenuSection | null)[];
  hasTabs?: boolean;
  activeTab?: string;
  isExpandable?: boolean;
  openTabValue?: string;
  searchable?: boolean;
}>();

const emit = defineEmits(["sidebarToggled", "update:activeTab", "tab-click"]);

const TRANSITION_DURATION = 500;

const tabOpenStates = ref<Record<string, boolean>>({});
const isExpanded = ref(true);
const showConfirmModal = ref(false);
const menuWrapperWidth = ref("auto");
const menuWrapper = ref<HTMLElement | null>(null);
const searchQuery = ref("");

const matchesSearch = (option: ISidebarSubmenuOption, query: string) => {
  if (option.label.toLowerCase().includes(query)) {
    return true;
  }

  if (option.tabs?.some((tab) => tab.toLowerCase().includes(query))) {
    return true;
  }

  if (
    option.additionalTabLinks?.some((tabLink) =>
      tabLink.label.toLowerCase().includes(query)
    )
  ) {
    return true;
  }

  return false;
};

const filteredOptions = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();

  if (!query) {
    return options;
  }

  return options
    .map((section) => {
      if (!section) {
        return null;
      }

      const filteredSectionOptions = section.options.filter((option) =>
        matchesSearch(option, query)
      );

      if (!filteredSectionOptions.length) {
        return null;
      }

      return {
        ...section,
        options: filteredSectionOptions,
      };
    })
    .filter(Boolean);
});

const submenuClass = computed(() => {
  const classList = ["bg-white", `duration-500`];

  if (isExpanded.value) {
    classList.push("min-w-max", "max-w-max");
  } else {
    classList.push("w-8", "-translate-x-5", "bg-gradient");
    return classList;
  }

  if (!hasTabs) {
    classList.push("w-[260px]");
    return classList;
  }

  classList.push(
    "w-[260px]",
    "min-w-50",
    "overflow-y-auto",
    "overflow-x-hidden"
  );
  return classList;
});

const isTabOpen = (key: string) => {
  if (key in tabOpenStates.value) {
    return tabOpenStates.value[key];
  }

  // If openTabValue not provided, default to open (original behavior)
  if (!openTabValue) {
    return true;
  }

  return openTabValue === key;
};

const setTabOpen = (key: string, value: boolean) => {
  tabOpenStates.value[key] = value;
};

const handleToggleClick = async () => {
  // When closing, preserving the width on the inner menu wrapper prevents it from being squished when the parent width changes
  if (isExpanded.value && menuWrapper.value) {
    const menuWidth = Math.ceil(
      menuWrapper.value.getBoundingClientRect().width
    );
    menuWrapperWidth.value = `${menuWidth}px`;
  }

  isExpanded.value = !isExpanded.value;
  emit("sidebarToggled", isExpanded.value);

  if (isExpanded.value) {
    setTimeout(() => {
      menuWrapperWidth.value = "auto";
    }, TRANSITION_DURATION);
  }
};

const isOptionActive = (option: ISidebarSubmenuOption) => {
  if (!option.link) {
    return false;
  }

  if (typeof option.link === "object") {
    const { name } = option.link;
    return matched.some((match) => match.name === name);
  }

  return path.includes(option.link);
};

const executeHandler = (option: ISidebarSubmenuOption) => {
  if (option.confirm?.handler) {
    option.confirm.handler();
  }
};

watch(
  () => openTabValue,
  () => {
    tabOpenStates.value = {};
  }
);

onMounted(() => {
  emit("sidebarToggled", isExpanded.value);
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.menu-wrapper {
  width: v-bind(menuWrapperWidth);
}
</style>
